"use client";

import { useState, useEffect, useMemo } from "react";
import { useParams, useRouter } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import styles from "./product-detail.module.scss";
import Loader from "../../components/loader/loader";
import { useDispatch, useSelector } from "react-redux";
import { addToCart, removeFromCart } from "../../../store/cartSlice";
import Button from "@/app/components/button/button";
import pStyles from "@/app/components/productRow.module.scss";
import Swal from "sweetalert2";
import axios from "axios";
import { FaStar } from "react-icons/fa";
import {
  Heart,
  ShoppingCart,
  Star,
  ShieldCheck,
  Truck,
  RotateCcw,
  Share2,
  Minus,
  Plus,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

export default function ProductDetailPage() {
  const params = useParams();
  const router = useRouter();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const [error, setError] = useState();
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated);
  const user = useSelector((state) => state.auth?.user);
  const [quantity, setQuantity] = useState(1);
  const [wishlist, setWishlist] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const fetchProduct = async () => {
    try {
      setLoading(true);
      const specificProduct = await axios.get(`/api/products/${params.id}`);
      const data = await specificProduct.data;
      setProduct(data);
      const relatedProducts = await axios.get(
        `/api/products?category=${data.category}`,
      );
      const relatedProductsData = await relatedProducts.data;
      setRelatedProducts(relatedProductsData.products);
    } catch (e) {
      setError(e?.response?.data?.error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchProduct();
  }, []);

  const galleryImages = useMemo(() => {
    if (!product) return [];
    // Support optional gallery array if present; otherwise fall back to single image
    const base =
      product.images &&
      Array.isArray(product.images) &&
      product.images.length > 0
        ? product.images
        : [product.imageUrl || product.image];
    // Filter falsy and duplicates
    return Array.from(new Set(base.filter(Boolean)));
  }, [product]);

  const handleDelete = async () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to undo this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        await axios.delete(`/api/products/${params.id}`);
        dispatch(removeFromCart(params.id));
        Swal.fire("Deleted!", "Your item has been removed.", "success");
        router.push("/products");
      }
    });
  };

  const addToCartHandler = async (newItem, qty = 1) => {
    const cartItem = { ...newItem, id: newItem._id };
    // add multiple times to respect quantity in current reducer
    for (let i = 0; i < qty; i += 1) {
      dispatch(addToCart(cartItem));
    }
    Swal.fire({
      icon: "success",
      title: "Added to cart",
      text: `${newItem.name} ×${qty}`,
      timer: 1400,
      showConfirmButton: false,
    });
    if (user && user._id) {
      try {
        await axios.post("/api/cart", {
          userId: user._id,
          productId: newItem._id,
          quantity: qty,
        });
      } catch (err) {
        console.error(
          "Error saving cart to DB:",
          err?.response?.data || err?.message,
        );
      }
    }
  };

  const handleBuyNow = async (item, qty = 1) => {
    await addToCartHandler(item, qty);
    router.push("/checkout");
  };

  const discountPercentage = useMemo(() => {
    if (!product) return 0;
    const { newPrice, price } = product;
    if (!newPrice || newPrice >= price) return 0;
    return Math.round(((price - newPrice) / price) * 100);
  }, [product]);

  const formatPrice = (value) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(Number(value || 0));

  const shareProduct = async () => {
    try {
      const shareData = {
        title: product?.name || "Product",
        text: product?.description?.slice(0, 80) || "Check this out",
        url: typeof window !== "undefined" ? window.location.href : "",
      };
      if (navigator.share) {
        await navigator.share(shareData);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareData.url);
        Swal.fire({
          icon: "success",
          title: "Link copied",
          timer: 1000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error("Error sharing product:", error);
    }
  };

  if (loading) return <Loader />;

  if (!product) {
    return (
      <div className={styles.error}>
        <h2>{error}</h2>
        <p>The product you are looking for does not exist.</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.productDetail}>
        {/* Gallery */}
        <div className={styles.gallery}>
          <div className={styles.thumbs}>
            {galleryImages.map((src, idx) => (
              <button
                key={`${src}-${idx}`}
                className={`${styles.thumb} ${
                  idx === activeImageIndex ? styles.activeThumb : ""
                }`}
                onClick={() => setActiveImageIndex(idx)}
                aria-label={`View image ${idx + 1}`}
              >
                <Image
                  src={src || "/image/imageFail.jpg"}
                  alt={`${product.name} thumbnail ${idx + 1}`}
                  width={72}
                  height={72}
                />
              </button>
            ))}
          </div>
          <div className={styles.mainImage}>
            <button
              className={styles.navLeft}
              onClick={() =>
                setActiveImageIndex((prev) =>
                  prev === 0 ? galleryImages.length - 1 : prev - 1,
                )
              }
              aria-label="Previous image"
            >
              <ChevronLeft size={18} />
            </button>
            <Image
              src={galleryImages[activeImageIndex] || "/image/imageFail.jpg"}
              alt={product.name || "product image"}
              width={800}
              height={800}
              priority
            />
            <button
              className={styles.navRight}
              onClick={() =>
                setActiveImageIndex((prev) =>
                  prev === galleryImages.length - 1 ? 0 : prev + 1,
                )
              }
              aria-label="Next image"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* Info / Purchase Panel */}
        <div className={styles.infoSection}>
          <h1 className={styles.header}>{product.name}</h1>

          <div className={styles.metaRow}>
            <span className={styles.category}>{product.category}</span>
            <span className={styles.dot}>•</span>
            <span className={styles.series}>{product.series}</span>
          </div>

          <div className={styles.ratingRow}>
            <div className={styles.stars}>
              {Array.from({ length: 5 }).map((_, i) => (
                <FaStar
                  key={i}
                  size={16}
                  className={
                    i < Math.floor(product.rating)
                      ? styles.starFilled
                      : i < product.rating
                        ? styles.starHalf
                        : ""
                  }
                />
              ))}
            </div>
            <span className={styles.reviewCount}>
              {product.reviewCount} reviews
            </span>
          </div>

          <div className={styles.priceRow}>
            <span className={styles.currentPrice}>
              {formatPrice(
                product.newPrice && product.newPrice < product.price
                  ? product.newPrice
                  : product.price,
              )}
            </span>
            {product.newPrice && product.newPrice < product.price && (
              <>
                <span className={styles.oldPrice}>
                  {formatPrice(product.price)}
                </span>
                <span className={styles.badgeOffer}>
                  {discountPercentage}% OFF
                </span>
              </>
            )}
          </div>

          <div className={styles.purchasePanel}>
            <div className={styles.quantityControl}>
              <button
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                aria-label="Decrease quantity"
              >
                <Minus size={16} />
              </button>
              <input
                type="number"
                min={1}
                value={quantity}
                onChange={(e) =>
                  setQuantity(Math.max(1, Number(e.target.value) || 1))
                }
                aria-label="Quantity"
              />
              <button
                onClick={() => setQuantity((q) => q + 1)}
                aria-label="Increase quantity"
              >
                <Plus size={16} />
              </button>
            </div>
            <div className={styles.ctaButtons}>
              <Button onClick={() => addToCartHandler(product, quantity)}>
                <ShoppingCart size={16} /> Add to Cart
              </Button>
              {product.inStock ? (
                <Button
                  onClick={() => handleBuyNow(product, quantity)}
                  variant="outlined"
                >
                  Buy Now
                </Button>
              ) : (
                <Button variant="outlined">Out of Stock</Button>
              )}
              <button className={styles.iconButton} onClick={shareProduct}>
                <Share2 size={16} />
              </button>{" "}
              <button
                className={`${styles.iconButton} ${
                  wishlist ? styles.wishActive : ""
                }`}
                onClick={() => setWishlist((w) => !w)}
                aria-label="Toggle wishlist"
                title="Add to wishlist"
              >
                <Heart size={18} />
              </button>
            </div>
          </div>

          <div className={styles.uspRow}>
            <div className={styles.uspItem}>
              <Truck size={18} /> Free Express Shipping
            </div>
            <div className={styles.uspItem}>
              <RotateCcw size={18} /> 30-Day Returns
            </div>
            <div className={styles.uspItem}>
              <ShieldCheck size={18} /> 1-Year Warranty
            </div>
          </div>

          <div className={styles.accordion}>
            <details open>
              <summary>Description</summary>
              <p>{product.description}</p>
            </details>
            <details>
              <summary>Specifications</summary>
              <ul>
                <li>
                  <strong>Product ID:</strong> {product._id}
                </li>
                <li>
                  <strong>Launched:</strong>{" "}
                  {new Date(product.createdAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </li>
                <li>
                  <strong>Series:</strong> {product.series}
                </li>
              </ul>
            </details>
          </div>

          {/* {isAuthenticated && (
            <div className={styles.adminActions}>
              <Button onClick={handleDelete}>Delete Product</Button>
            </div>
          )} */}
        </div>
      </div>

      <div className={styles.moreSimiler}>
        <h4 className={styles.heading}>You May Also Like</h4>
        <section className={pStyles.productRow}>
          {relatedProducts
            .filter((p) => p._id !== product._id)
            .slice(0, 8)
            .map((p) => (
              <Link
                href={`/products/${p._id || p.slug || encodeURIComponent(p.name)}`}
                key={p._id || p.slug || p.name}
                className={pStyles.productCard}
              >
                <div>
                  <Image
                    src={p.imageUrl || "/image/imageFail.jpg"}
                    alt={p.name}
                    width={400}
                    height={400}
                  />
                </div>
                <h4>{p.name}</h4>
                <span className={pStyles.category}>{p.category}</span>
                <p>
                  MRP:{" "}
                  {formatPrice(
                    p.newPrice && p.newPrice < p.price ? p.newPrice : p.price,
                  )}
                </p>
              </Link>
            ))}
        </section>
      </div>
    </div>
  );
}
